/**
 * THE JOIN GATE'S SETUP BRANCH, as a pure decision (spec
 * underwriter/2026-10-17-001 R7, D29, §9.2).
 *
 * Staff applied an AUTOMATIC payment setup to a product-rail organisation and
 * nothing is paid yet: the gate status answers `requiresPaymentReason =
 * "billing_setup_due"`. An admin is then taken to the product's own plan page
 * to pay; a member waits on the billing-pending page. Everything is read from
 * the registry row through `paymentSetupSurfaceOf`, so no product is named
 * here and product #4 inherits the branch by declaring its row.
 *
 * `billing_setup_due` is consumed HERE ONLY. The broker `/payment` redirect and
 * the paid-rung wall never read it (R7 AC6).
 */
import type { PaymentSetupSurface } from "@/domain/scope/platform-scope"
import type { BillingGateStatusResponse } from "@/contexts/billing/core/presentation/rpc/billing-lockout-rpc"

export type SetupGateDecision = "pass" | "redirect" | "pending"

/** The one reason the setup branch acts on. */
export const SETUP_DUE_REASON = "billing_setup_due" as const satisfies NonNullable<
  (typeof BillingGateStatusResponse.Type)["requiresPaymentReason"]
>

/** The slice of the gate status this branch reads (an older cached payload may lack the field). */
export interface SetupGateStatus {
  readonly requiresPaymentReason?: (typeof BillingGateStatusResponse.Type)["requiresPaymentReason"]
}

export interface SetupGateInput {
  readonly pathname: string
  readonly surface: PaymentSetupSurface | null
  readonly gateStatus: SetupGateStatus | null | undefined
  readonly isAdmin: boolean
  readonly skipGates: boolean
}

/** Segment-boundary prefix match, the rule `scopeForPath` and the wall use. */
const onPath = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

/** Is this path one of the setup surfaces, which never wall (R7 AC3)? */
export const isSetupExemptPath = (pathname: string, surface: PaymentSetupSurface | null): boolean =>
  surface !== null && surface.exempt.some((prefix) => onPath(pathname, prefix))

/** Where the redirect sends an admin: the plan page, in required mode (D48). */
export const setupRedirectTarget = (surface: PaymentSetupSurface): string => `${surface.page}?setup=required`

/** Is a staff-applied automatic setup awaiting payment? A missing status fails open (D11). */
export const isSetupDue = (gateStatus: SetupGateStatus | null | undefined): boolean =>
  gateStatus?.requiresPaymentReason === SETUP_DUE_REASON

export const setupGateOf = ({ pathname, surface, gateStatus, isAdmin, skipGates }: SetupGateInput): SetupGateDecision => {
  if (surface === null || skipGates || isSetupExemptPath(pathname, surface) || !isSetupDue(gateStatus)) {
    return "pass"
  }
  return isAdmin ? "redirect" : "pending"
}
